import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ButtonVariant, Spinner } from '@shipengine/giger';
import { Currency, SubscriptionPlan } from '@packlink/packlink-sdk';
import { useSubscriptionPlans } from '@Subscriptions/hooks/useClientSubscriptions';
import { planTypes } from '@common/hooks/usePlanFeatures';
import { usePlanPrice } from '@common/hooks/usePlanPrice';
import { useSidePanel } from '@common/hooks/useSidePanel';
import { PanelName } from '@types';
import { PlanCard, PlanInfo } from '@Subscriptions/components/PlanCard/PlanCard';
import { getPlanCardsContainerStyles, getSpinnerStyles } from './SelectPlanStyles';
import { AmplitudeEvents } from '@constants/amplitude';
import { GTMEvents } from '@packlink/metrics';
import { GTMActionFieldOption } from '@constants/gtm';
import { SUBSCRIPTION_PLAN } from '@Subscriptions/constants';
import { useAmplitude, useGoogleTagManager } from '@common/hooks';

type SelectPlanProps = {
    nextPage?: string;
    isOnboarding?: boolean;
};

export const SelectPlan = ({ nextPage, isOnboarding = false }: SelectPlanProps): JSX.Element => {
    const navigate = useNavigate();
    const { sendAmplitudeClickEvent } = useAmplitude();
    const { sendGtmEvent } = useGoogleTagManager();
    const [isFreePending, setIsFreePending] = useState(false);
    const { subscriptionPlans, isLoading } = useSubscriptionPlans();
    const { open: openSubscriptionPanel } = useSidePanel(PanelName.SUBSCRIPTIONS);

    const onClickPlus = () => {
        sendAmplitudeClickEvent(AmplitudeEvents.SUBSCRIPTION_GET_STARTED_NOW);

        sendGtmEvent(GTMEvents.UPGRADE_ACCOUNT, {
            action: GTMActionFieldOption.UPGRADE_ACCOUNT_SELECTED,
            typeOfPlan: SUBSCRIPTION_PLAN.PRO_PLUS,
        });
        openSubscriptionPanel();
    };

    // These will be static until we have multiple plans available
    const plans: Array<PlanInfo> = [
        {
            name: planTypes.FREE,
            price: Currency.format(0, 'EUR'),
            buttonVariant: ButtonVariant.OUTLINED,
            buttonAction: () => {
                if (isFreePending || !nextPage) return;

                setIsFreePending(true);

                // UX/Product requirement, simulates a delay for the free plan selection.
                setTimeout(() => {
                    navigate(nextPage ?? '');
                }, 1000);
            },
            selected: !isOnboarding,
            loading: isFreePending,
        },
        {
            name: planTypes.PLUS,
            price: usePlanPrice(subscriptionPlans?.[0] as SubscriptionPlan),
            buttonVariant: ButtonVariant.FILLED,
            buttonAction: onClickPlus,
            disabled: isFreePending,
        },
    ];

    return isLoading ? (
        <Spinner css={getSpinnerStyles} />
    ) : (
        <section css={getPlanCardsContainerStyles(isOnboarding)}>
            {plans.map((plan) => (
                <PlanCard plan={plan} key={plan.name} />
            ))}
        </section>
    );
};
